import type { Workout } from "./schemas/workout.js";
import { effectiveRir } from "./effortScale.js";
import type { LastLoggedSet } from "./resolveDefaultWeight.js";

// Derive a per-slug "last logged weighted SET" map from a recent-window
// of assembled workouts (newest-first). Drives the history step of
// resolveDefaultWeight at plan-start. Pure — no fetch, no React; the
// caller supplies the window (mobile reuses the EntryHistoryProvider
// cache, web can plug a fresh fetch in once Phase 4 lifts the bug-fix-
// only freeze).
//
// "Last logged" semantics: the most recent weighted set the athlete
// performed on the slug, picked by walking workouts newest-first and
// taking the last weighted set in the first matching entry. We DON'T
// take session-top — the prefill expectation is "the weight I ended on
// last time", which matches the lifter's wallet of carry-forward
// (next set = previous set's number until they change it). Failed sets
// still count: the lifter tried that load.
// ⚠️ Returns the reps alongside the kg (was: kg alone). The weight on its own
// answers only "what did I lift last time"; the effort arm of the chain asks
// "what load reaches RIR 2 for 8?", and to answer it the anchor must first
// become an e1RM — which needs the reps. They were already in hand here, one
// line from being dropped on the floor (PG-9, measurement-contract.md §17).
// AP-9b: with `{ bodyweight: true }` a bodyweight set counts too, reps and
// no kg: a pull-up logged 50 times has a last time, it just has no load.
// ⛔ Opt-in, and the weighted rows are read exactly as before: every caller
// that feeds the weight chain keeps the plain call, which never returns a
// null kg.
// AP-9c: the bodyweight row keeps its extra (signed, kg), present only when
// the set had one: a weighted pull-up is «8 reps +14 kg», not «8 reps».
export type LastLoggedAnySet =
  | LastLoggedSet
  | { weightKg: null; reps: number; extraKg?: number };

type WorkoutEntry = Workout["entries"][number];

const repsOf = (reps: unknown): number =>
  typeof reps === "number" && Number.isFinite(reps) ? reps : 0;

// The per-entry walk, exported so a caller that holds one entry (the indexed
// per-exercise door, not a window) reads it by the same rule as the window.
export function lastLoggedSetFromEntry(entry: WorkoutEntry): LastLoggedSet | undefined;
export function lastLoggedSetFromEntry(
  entry: WorkoutEntry,
  opts: { bodyweight: true },
): LastLoggedAnySet | undefined;
export function lastLoggedSetFromEntry(
  entry: WorkoutEntry,
  opts?: { bodyweight: true },
): LastLoggedAnySet | undefined {
  // Walk the entry's sets back-to-front to find the last weighted
  // logged value. Cardio sets don't expose a `weight`, and bodyweight
  // sets are read only when asked for; skipped slugs are "no history"
  // and the chain falls back to 'blank' at the logger.
  for (let i = entry.sets.length - 1; i >= 0; i -= 1) {
    const set = entry.sets[i];
    if (!set) continue;
    if (set.type === "bodyweight" && opts?.bodyweight) {
      const extra = set.extra;
      return {
        weightKg: null,
        reps: repsOf(set.reps),
        ...(typeof extra === "number" && Number.isFinite(extra) && extra !== 0
          ? { extraKg: extra }
          : {}),
      };
    }
    if (set.type !== "weighted") continue;
    if (typeof set.weight !== "number") continue;
    if (!Number.isFinite(set.weight)) continue;
    if (set.weight <= 0) continue;
    // Reps ride along for the effort arm. A set without a usable rep
    // count still prefills the weight (reps 0 simply fails the Epley
    // eligibility gate downstream): the history prefill must not start
    // refusing rows it has always accepted.
    // D9: the effort rides along, so the chain reads the e1RM at it.
    const rir = effectiveRir(set);
    return {
      weightKg: set.weight,
      reps: repsOf(set.reps),
      ...(rir !== null ? { rir } : {}),
    };
  }
  return undefined;
}

export function lastLoggedSetsFromWorkouts(
  workouts: ReadonlyArray<Workout>,
  slugs: ReadonlyArray<string>,
): Map<string, LastLoggedSet>;
export function lastLoggedSetsFromWorkouts(
  workouts: ReadonlyArray<Workout>,
  slugs: ReadonlyArray<string>,
  opts: { bodyweight: true },
): Map<string, LastLoggedAnySet>;
export function lastLoggedSetsFromWorkouts(
  workouts: ReadonlyArray<Workout>,
  slugs: ReadonlyArray<string>,
  opts?: { bodyweight: true },
): Map<string, LastLoggedAnySet> {
  const out = new Map<string, LastLoggedAnySet>();
  if (slugs.length === 0 || workouts.length === 0) return out;
  const wanted = new Set(slugs);
  for (const w of workouts) {
    for (const entry of w.entries) {
      if (!wanted.has(entry.exerciseId)) continue;
      if (out.has(entry.exerciseId)) continue;
      const last = opts
        ? lastLoggedSetFromEntry(entry, opts)
        : lastLoggedSetFromEntry(entry);
      if (last !== undefined) out.set(entry.exerciseId, last);
    }
    if (out.size === wanted.size) break;
  }
  return out;
}

/** The weighted half of a mixed map: what the weight chain may read. */
export const weightedLastSets = (
  all: ReadonlyMap<string, LastLoggedAnySet>,
): Map<string, LastLoggedSet> => {
  const out = new Map<string, LastLoggedSet>();
  for (const [slug, set] of all) {
    if (set.weightKg !== null) {
      out.set(slug, {
        weightKg: set.weightKg,
        reps: set.reps,
        ...(set.rir != null ? { rir: set.rir } : {}),
      });
    }
  }
  return out;
};

/** AP-9c: the weighted half plus every bodyweight row with an extra, the
 *  extra as its load. The chain reads history on a bodyweight row as an extra
 *  already (planStart.ts), so the built draft opens with «+14 kg» filled in. */
export const lastSetsWithExtra = (
  all: ReadonlyMap<string, LastLoggedAnySet>,
): Map<string, LastLoggedSet> => {
  const out = weightedLastSets(all);
  for (const [slug, set] of all) {
    if (set.weightKg === null && set.extraKg !== undefined) {
      out.set(slug, { weightKg: set.extraKg, reps: set.reps });
    }
  }
  return out;
};
